import { useCallback, useRef } from 'react';

// Scroll priority definition (lower number = higher priority)
export enum ScrollPriority {
  USER_MANUAL = 0, // User manual operation
  INFINITE_RESTORE = 1, // Infinite scroll position restore
  UNREAD_NAVIGATION = 2, // Navigate to unread messages
  SEND_AUTO_SCROLL = 3, // Auto-scroll after sending message
  GENERAL_AUTO_SCROLL = 4, // General auto-scroll
  IOS_KEYBOARD = 5, // iOS keyboard adjustment
}

export type ScrollAction = {
  id: string;
  priority: ScrollPriority;
  action: () => void | Promise<void>;
  timeout?: number;
  description?: string;
};

export type ScrollState = {
  mode: 'idle' | 'processing' | 'blocked';
  currentAction: ScrollAction | null;
  queue: ScrollAction[];
};

export const SCROLL_CONFIG = {
  thresholds: {
    nearBottom: 50,
    veryNearBottom: 10,
    scrollTolerance: 5,
  },
  timing: {
    scrollDelay: 200,
    verificationDelay: 50,
    debounceDelay: 100,
    blockDuration: 300,
    smoothScrollDuration: 0,
  },
  limits: {
    maxVerificationAttempts: 2,
    maxRestoreAttempts: 20,
    queueTimeout: 5000,
  },
} as const;

/**
 * Centralized scroll conflict prevention hook
 * Manages scroll actions using priority-based queue system
 */
export const useScrollActionManager = () => {
  const stateRef = useRef<ScrollState>({
    mode: 'idle',
    currentAction: null,
    queue: [],
  });

  const timeoutRef = useRef<NodeJS.Timeout>();
  const blockTimeoutRef = useRef<NodeJS.Timeout>();
  const actionCounter = useRef(0);

  // Generate unique action ID
  const generateActionId = useCallback(() => {
    actionCounter.current += 1;
    return `scroll_${Date.now()}_${actionCounter.current}`;
  }, []);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (blockTimeoutRef.current) {
      clearTimeout(blockTimeoutRef.current);
      blockTimeoutRef.current = undefined;
    }
  }, []);

  // Process actions from queue sequentially
  const processQueue = useCallback(async () => {
    const state = stateRef.current;

    // Exit early if not idle or queue is empty
    if (state.mode !== 'idle' || state.queue.length === 0) {
      return;
    }

    // Sort by priority (lower number = higher priority)
    state.queue.sort((a, b) => a.priority - b.priority);

    const nextAction = state.queue.shift()!;
    state.mode = 'processing';
    state.currentAction = nextAction;

    try {
      await nextAction.action();

      // Block briefly to prevent conflicts
      state.mode = 'blocked';
      blockTimeoutRef.current = setTimeout(() => {
        state.mode = 'idle';
        state.currentAction = null;

        // Continue processing if more actions exist
        if (state.queue.length > 0) {
          processQueue();
        }
      }, SCROLL_CONFIG.timing.blockDuration);
    } catch {
      // Continue to next action on error
      state.mode = 'idle';
      state.currentAction = null;

      if (state.queue.length > 0) {
        setTimeout(() => processQueue(), SCROLL_CONFIG.timing.debounceDelay);
      }
    }
  }, []);

  // Add scroll action to queue
  const addScrollAction = useCallback(
    (
      action: () => void | Promise<void>,
      priority: ScrollPriority,
      options?: {
        timeout?: number;
        description?: string;
        force?: boolean; // Force execution flag
      },
    ) => {
      const state = stateRef.current;
      const scrollAction: ScrollAction = {
        id: generateActionId(),
        priority,
        action,
        timeout: options?.timeout || SCROLL_CONFIG.limits.queueTimeout,
        description: options?.description,
      };

      // Interrupt if forced or higher priority than current action
      const shouldInterrupt =
        options?.force || (state.currentAction && priority < state.currentAction.priority);

      if (shouldInterrupt) {
        clearTimers();
        state.mode = 'idle';
        state.currentAction = null;
        state.queue.unshift(scrollAction);
      } else {
        // Add to queue
        state.queue.push(scrollAction);

        // Remove duplicate lower priority actions if not idle
        if (state.mode !== 'idle') {
          removeDuplicateActions(state, scrollAction);
        }
      }

      // Cleanup expired actions
      cleanupExpiredActions(state);

      // Start processing immediately if idle
      if (state.mode === 'idle') {
        processQueue();
      }

      return scrollAction.id;
    },
    [generateActionId, clearTimers, processQueue],
  );

  // Remove duplicate lower priority actions
  const removeDuplicateActions = (state: ScrollState, newAction: ScrollAction) => {
    const existingIndex = state.queue.findIndex(
      (existing, index) =>
        index !== state.queue.length - 1 &&
        existing.priority >= newAction.priority &&
        existing.description === newAction.description,
    );

    if (existingIndex >= 0) {
      state.queue.splice(existingIndex, 1);
    }
  };

  // Cleanup expired actions
  const cleanupExpiredActions = (state: ScrollState) => {
    const now = Date.now();
    state.queue = state.queue.filter((action) => {
      if (!action.timeout) {
        return true;
      }

      const actionTimestamp = parseInt(action.id.split('_')[1]);
      return now - actionTimestamp <= action.timeout;
    });
  };

  // Cancel action by ID
  const cancelAction = useCallback((actionId: string) => {
    const state = stateRef.current;
    const index = state.queue.findIndex((action) => action.id === actionId);

    if (index >= 0) {
      state.queue.splice(index, 1);
      return true;
    }

    return false;
  }, []);

  // Cancel all actions
  const cancelAllActions = useCallback(() => {
    const state = stateRef.current;

    state.queue = [];
    clearTimers();

    if (state.mode !== 'idle') {
      state.mode = 'idle';
      state.currentAction = null;
    }
  }, [clearTimers]);

  // Check if scroll is in progress
  const isScrollInProgress = useCallback(() => {
    return stateRef.current.mode !== 'idle';
  }, []);

  // Get currently executing action
  const getCurrentAction = useCallback(() => {
    return stateRef.current.currentAction;
  }, []);

  // Get queue status (for debugging)
  const getQueueStatus = useCallback(() => {
    const state = stateRef.current;
    return {
      mode: state.mode,
      queueLength: state.queue.length,
      currentAction: state.currentAction?.description || state.currentAction?.id,
      pendingActions: state.queue.map((action) => ({
        id: action.id,
        priority: action.priority,
        description: action.description,
      })),
    };
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    clearTimers();
    cancelAllActions();
  }, [clearTimers, cancelAllActions]);

  return {
    addScrollAction,
    cancelAction,
    cancelAllActions,
    isScrollInProgress,
    getCurrentAction,
    getQueueStatus,
    cleanup,
    SCROLL_CONFIG,
    ScrollPriority,
  };
};
